import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { CreditCard, Banknote, Lock, Landmark } from "lucide-react";
import { toast } from "sonner";
import api, { eur, formatApiErrorDetail } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";

export default function Checkout() {
  const { items, subtotal, clearCart } = useCart();
  const { user } = useAuth();
  const { settings } = useSettings();
  const nav = useNavigate();
  const pay = settings?.payment || { cod_enabled: true, stripe_enabled: true, bank_enabled: false };
  const [payment, setPayment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || "", email: user?.email || "", phone: "",
    address: "", city: "", postal_code: "", notes: "",
  });

  useEffect(() => {
    if (payment) return;
    if (pay.cod_enabled) setPayment("cod");
    else if (pay.stripe_enabled) setPayment("stripe");
    else if (pay.bank_enabled) setPayment("bank");
  }, [pay, payment]);

  const threshold = settings?.free_shipping_threshold ?? 50;
  const shipCost = settings?.shipping_cost ?? 0;
  const shipping = useMemo(() => (subtotal >= threshold ? 0 : shipCost), [subtotal, threshold, shipCost]);
  const total = subtotal + shipping;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  if (items.length === 0) return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center">
      <p className="text-[#536D61]">Το καλάθι σας είναι άδειο.</p>
      <Link to="/shop" className="text-[#1E3A2F] underline mt-4 inline-block">Δείτε τα προϊόντα</Link>
    </div>
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.address || !form.city || !form.postal_code) {
      toast.error("Συμπληρώστε όλα τα υποχρεωτικά πεδία");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        customer: form,
        payment_method: payment,
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/checkout", payload);
      if (data.payment_method === "stripe" && data.url) {
        window.location.href = data.url;
        return;
      }
      clearCart();
      if (data.payment_method === "bank") {
        nav(`/order/success?order_id=${data.order_id}&method=bank`);
      } else {
        nav(`/order/success?order_id=${data.order_id}&cod=1`);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Σφάλμα");
      setSubmitting(false);
    }
  };

  const inputCls = "w-full px-4 py-3 rounded-sm border border-[#E5E9E4] bg-white text-sm focus:border-[#1E3A2F] focus:ring-1 focus:ring-[#1E3A2F] outline-none";

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
      <h1 className="font-serif-display text-4xl text-[#1E3A2F] mb-8">Ολοκλήρωση παραγγελίας</h1>
      <form onSubmit={submit} className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-8">
          {!user && (
            <div className="bg-[#E8F0E1] border border-[#CBD9B6] rounded-sm p-4 text-sm text-[#1E3A2F]">
              Έχετε λογαριασμό; <Link to="/login" className="underline font-medium">Συνδεθείτε</Link> ή συνεχίστε ως επισκέπτης.
            </div>
          )}

          <div>
            <h2 className="font-semibold text-[#1E3A2F] text-lg mb-4">Στοιχεία παράδοσης</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <input data-testid="checkout-name" placeholder="Ονοματεπώνυμο *" value={form.name} onChange={set("name")} className={inputCls} />
              <input data-testid="checkout-email" type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} />
              <input data-testid="checkout-phone" placeholder="Τηλέφωνο *" value={form.phone} onChange={set("phone")} className={inputCls} />
              <input data-testid="checkout-postal" placeholder="Τ.Κ. *" value={form.postal_code} onChange={set("postal_code")} className={inputCls} />
              <input data-testid="checkout-address" placeholder="Διεύθυνση *" value={form.address} onChange={set("address")} className={`${inputCls} sm:col-span-2`} />
              <input data-testid="checkout-city" placeholder="Πόλη *" value={form.city} onChange={set("city")} className={inputCls} />
            </div>
            <textarea data-testid="checkout-notes" placeholder="Σημειώσεις παραγγελίας (προαιρετικά)" value={form.notes} onChange={set("notes")} rows={3} className={`${inputCls} mt-4`} />
          </div>

          <div>
            <h2 className="font-semibold text-[#1E3A2F] text-lg mb-4">Τρόπος πληρωμής</h2>
            <div className="space-y-3">
              {pay.cod_enabled && (
              <label data-testid="payment-cod" className={`flex items-center gap-3 p-4 rounded-sm border cursor-pointer transition-colors ${payment === "cod" ? "border-[#1E3A2F] bg-[#E8F0E1]" : "border-[#E5E9E4] bg-white"}`}>
                <input type="radio" name="payment" checked={payment === "cod"} onChange={() => setPayment("cod")} className="accent-[#1E3A2F]" />
                <Banknote className="h-5 w-5 text-[#1E3A2F]" />
                <div>
                  <p className="font-medium text-[#1E3A2F]">Αντικαταβολή</p>
                  <p className="text-sm text-[#536D61]">Πληρωμή με μετρητά κατά την παράδοση</p>
                </div>
              </label>
              )}
              {pay.stripe_enabled && (
              <label data-testid="payment-stripe" className={`flex items-center gap-3 p-4 rounded-sm border cursor-pointer transition-colors ${payment === "stripe" ? "border-[#1E3A2F] bg-[#E8F0E1]" : "border-[#E5E9E4] bg-white"}`}>
                <input type="radio" name="payment" checked={payment === "stripe"} onChange={() => setPayment("stripe")} className="accent-[#1E3A2F]" />
                <CreditCard className="h-5 w-5 text-[#1E3A2F]" />
                <div>
                  <p className="font-medium text-[#1E3A2F]">Κάρτα (online)</p>
                  <p className="text-sm text-[#536D61]">Ασφαλής πληρωμή μέσω Stripe</p>
                </div>
              </label>
              )}
              {pay.bank_enabled && (
              <label data-testid="payment-bank" className={`flex items-center gap-3 p-4 rounded-sm border cursor-pointer transition-colors ${payment === "bank" ? "border-[#1E3A2F] bg-[#E8F0E1]" : "border-[#E5E9E4] bg-white"}`}>
                <input type="radio" name="payment" checked={payment === "bank"} onChange={() => setPayment("bank")} className="accent-[#1E3A2F]" />
                <Landmark className="h-5 w-5 text-[#1E3A2F]" />
                <div>
                  <p className="font-medium text-[#1E3A2F]">Κατάθεση σε τραπεζικό λογαριασμό</p>
                  <p className="text-sm text-[#536D61]">Θα λάβετε τα στοιχεία του λογαριασμού μετά την παραγγελία</p>
                </div>
              </label>
              )}
            </div>
            {payment === "bank" && pay.bank_details?.iban && (
              <div className="mt-3 bg-[#E8F0E1] border border-[#CBD9B6] rounded-sm p-4 text-sm text-[#1E3A2F]">
                <p className="font-medium mb-1">Στοιχεία κατάθεσης:</p>
                {pay.bank_details.bank_name && <p>Τράπεζα: {pay.bank_details.bank_name}</p>}
                {pay.bank_details.account_name && <p>Δικαιούχος: {pay.bank_details.account_name}</p>}
                <p>IBAN: <span className="font-mono">{pay.bank_details.iban}</span></p>
                {pay.bank_details.notes && <p className="mt-1 text-[#536D61]">{pay.bank_details.notes}</p>}
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="bg-white border border-[#E5E9E4] rounded-sm p-6 sticky top-28">
            <h2 className="font-serif-display text-2xl text-[#1E3A2F] mb-4">Η παραγγελία σας</h2>
            <div className="space-y-3 max-h-64 overflow-auto mb-4">
              {items.map((it) => (
                <div key={it.product_id} className="flex justify-between gap-2 text-sm">
                  <span className="text-[#2a3a33] line-clamp-1">{it.name} × {it.quantity}</span>
                  <span className="font-medium whitespace-nowrap">{eur(it.price * it.quantity)}</span>
                </div>
              ))}
            </div>
            <div className="flex justify-between text-sm py-2 border-t border-[#E5E9E4]"><span className="text-[#536D61]">Υποσύνολο</span><span>{eur(subtotal)}</span></div>
            <div className="flex justify-between text-sm py-2"><span className="text-[#536D61]">Μεταφορικά</span><span>{shipping === 0 ? "Δωρεάν" : eur(shipping)}</span></div>
            <div className="flex justify-between py-3 text-lg font-semibold text-[#1E3A2F] border-t border-[#E5E9E4]"><span>Σύνολο</span><span data-testid="checkout-total">{eur(total)}</span></div>
            <button type="submit" disabled={submitting} data-testid="place-order-button" className="w-full bg-[#1E3A2F] text-white py-3.5 rounded-sm font-medium hover:bg-[#111C18] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
              <Lock className="h-4 w-4" /> {submitting ? "Επεξεργασία..." : payment === "stripe" ? "Πληρωμή με κάρτα" : "Οριστικοποίηση παραγγελίας"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
